import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useGoogleAnalytics, trackEvent } from '../../hooks/useGoogleAnalytics';
import { seoService } from '../../services/seoService';
import { agentAvailabilityService } from '../../services/appointmentService';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import AnalyticsOverview from './components/AnalyticsOverview';
import SEOChecklist from './components/SEOChecklist';
import LocalSEOPerformance from './components/LocalSEOPerformance';
import ConversionTracking from './components/ConversionTracking';
import SchemaStatusWidget from './components/SchemaStatusWidget';
import { BarChart3, CheckCircle2, TrendingUp, MapPin, Target, Activity, Calendar } from 'lucide-react';
import Icon from '../../components/AppIcon';


const SEODashboard = () => {
  useGoogleAnalytics();
  const navigate = useNavigate();

  const [auditItems, setAuditItems] = useState([]);
  const [schemaMarkup, setSchemaMarkup] = useState([]);
  const [localPerformance, setLocalPerformance] = useState([]);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);


  useEffect(() => {
    loadDashboardData();
    trackEvent('page_view', { page_name: 'SEO Dashboard' });
  }, []);
  useEffect(() => {
  checkAuth();
}, []);

  useEffect(() => {
  if (user && activeTab !== 'agent-availability') {
    loadDashboardData();
    trackEvent('page_view', { page_name: 'SEO Dashboard' });
  }
}, [user, activeTab]);
const checkAuth = async () => {
  const { session } = await authService.getSession();
  
  if (!session) {
    navigate('/staff-login');
    return;
  }

  const { user } = await authService.getCurrentUser();
  setUser(user);
  setAuthLoading(false);
};

const handleLogout = async () => {
  if (confirm('Are you sure you want to log out?')) {
    await authService.signOut();
    navigate('/staff-login');
  }
};



  const loadDashboardData = async () => {
    setLoading(true);
    try {
      const [auditResult, schemaResult, localResult, analyticsResult] = await Promise.all([
        seoService?.getAuditItems(),
        seoService?.getSchemaMarkup({ isActive: true }),
        seoService?.getLocalPerformance({ county: 'Sacramento County' }),
        seoService?.getAnalyticsSummary({
          startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)?.toISOString()
        })
      ]);

      setAuditItems(auditResult?.data || []);
      setSchemaMarkup(schemaResult?.data || []);
      setLocalPerformance(localResult?.data || []);
      setAnalyticsData(analyticsResult?.data || []);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    trackEvent('tab_change', { tab_name: tab, section: 'SEO Dashboard' });
  };

  const tabs = [
    { id: 'overview', label: 'Analytics Overview', icon: BarChart3 },
    { id: 'checklist', label: 'SEO Checklist', icon: CheckCircle2 },
    { id: 'local', label: 'Local Performance', icon: MapPin },
    { id: 'conversions', label: 'Conversions', icon: Target },
    { id: 'schema', label: 'Schema Status', icon: Activity },
    { id: 'agent-availability', label: 'Agent Availability', icon: Calendar }
  ];
if (authLoading) {
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
        <p className="text-gray-600">Verifying authentication...</p>
      </div>
    </div>
  );
}

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>SEO Dashboard & Analytics Hub - Sailors Bail Bonds</title>
        <meta 
          name="description" 
          content="Comprehensive SEO monitoring and analytics dashboard for Sacramento County bail bonds marketing performance tracking." 
        />
      </Helmet>
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="mb-8">
  <div className="flex items-center justify-between mb-4">
    <div className="flex items-center gap-3">
      <TrendingUp className="w-8 h-8 text-blue-600" />
      <div>
        <h1 className="text-3xl font-bold text-gray-900">
          SEO Dashboard & Analytics Hub
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          Logged in as: {user?.email}
        </p>
      </div>
    </div>
    <button
      onClick={handleLogout}
      className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
    >
      <LogOut className="w-4 h-4" />
      Logout
    </button>
  </div>

          <p className="text-lg text-gray-600">
            Real-time monitoring of search engine optimization performance and analytics for Sacramento County bail bonds
          </p>
        </div>

        {/* Navigation Tabs */}
        <div className="bg-white rounded-lg shadow-sm mb-6 overflow-hidden">
          <div className="flex flex-wrap border-b border-gray-200">
            {tabs?.map((tab) => {
              const Icon = tab?.icon;
              return (
                <button
                  key={tab?.id}
                  onClick={() => handleTabChange(tab?.id)}
                  className={`flex items-center gap-2 px-6 py-4 font-medium transition-colors ${
                    activeTab === tab?.id
                      ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50' :'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span className="hidden sm:inline">{tab?.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Loading State */}
        {loading && activeTab !== 'agent-availability' && (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        )}

        {/* Tab Content */}
        {!loading && (
          <div className="space-y-6">
            {activeTab === 'overview' && (
              <AnalyticsOverview 
                analyticsData={analyticsData}
                auditItems={auditItems}
                onRefresh={loadDashboardData}
              />
            )}

            {activeTab === 'checklist' && (
              <SEOChecklist 
                auditItems={auditItems}
                onUpdate={loadDashboardData}
              />
            )}

            {activeTab === 'local' && (
              <LocalSEOPerformance 
                performanceData={localPerformance}
                onRefresh={loadDashboardData}
              />
            )}

            {activeTab === 'conversions' && (
              <ConversionTracking 
                analyticsData={analyticsData}
                onRefresh={loadDashboardData}
              />
            )}

            {activeTab === 'schema' && (
              <SchemaStatusWidget 
                schemaMarkup={schemaMarkup}
                onRefresh={loadDashboardData}
              />
            )}
          </div>
        )}

        {/* Agent Availability Management Tab */}
        {activeTab === 'agent-availability' && (
          <AgentAvailabilityManagement />
        )}

        {/* Emergency Contact Banner */}
        <div className="mt-8 bg-gradient-to-r from-blue-600 to-blue-700 rounded-lg p-6 text-white">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h3 className="text-xl font-bold mb-1">24/7 Emergency Bail Assistance</h3>
              <p className="text-blue-100">Sacramento County experts ready to help</p>
            </div>
            <a
              href="tel:916-555-BAIL"
              onClick={() => trackEvent('emergency_call', { source: 'SEO Dashboard' })}
              className="bg-white text-blue-600 px-6 py-3 rounded-lg font-bold hover:bg-blue-50 transition-colors"
            >
              Call Now: 916-555-BAIL
            </a>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

// Agent Availability Management Component
const AgentAvailabilityManagement = () => {
  const [agents, setAgents] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [weeklySchedule, setWeeklySchedule] = useState([]);
  const [timeOffRecords, setTimeOffRecords] = useState([]);
  const [scheduleOverrides, setScheduleOverrides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [subTab, setSubTab] = useState('weekly');

  const daysOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  useEffect(() => {
    loadAgents();
  }, []);

  useEffect(() => {
    if (selectedAgent) {
      loadAgentData();
    }
  }, [selectedAgent]);

  const loadAgents = async () => {
    try {
      const data = await agentAvailabilityService.getAllAgents();
      setAgents(data);
      if (data.length > 0) {
        setSelectedAgent(data[0].id);
      }
      setLoading(false);
    } catch (error) {
      console.error('Error loading agents:', error);
      setLoading(false);
    }
  };

  const loadAgentData = async () => {
    try {
      const [schedule, timeOff, overrides] = await Promise.all([
        agentAvailabilityService.getAgentWeeklySchedule(selectedAgent),
        agentAvailabilityService.getAgentTimeOff(selectedAgent),
        agentAvailabilityService.getScheduleOverrides(selectedAgent)
      ]);
      setWeeklySchedule(schedule);
      setTimeOffRecords(timeOff);
      setScheduleOverrides(overrides);
    } catch (error) {
      console.error('Error loading agent data:', error);
    }
  };

  const handleScheduleUpdate = async (dayOfWeek, field, value) => {
    try {
      const daySchedule = weeklySchedule.find(s => s.day_of_week === dayOfWeek);
      const updatedSchedule = {
        ...daySchedule,
        [field]: value
      };

      await agentAvailabilityService.updateAgentDaySchedule(
        selectedAgent,
        dayOfWeek,
        updatedSchedule
      );

      const schedule = await agentAvailabilityService.getAgentWeeklySchedule(selectedAgent);
      setWeeklySchedule(schedule);
      
      alert('Schedule updated successfully!');
    } catch (error) {
      console.error('Error updating schedule:', error);
      alert('Error updating schedule. Please try again.');
    }
  };

  const selectedAgentName = agents.find(a => a.id === selectedAgent)?.full_name || '';

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow">
      {/* Agent Selection */}
      <div className="p-6 border-b border-gray-200">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Select Agent
        </label>
        <select
          value={selectedAgent || ''}
          onChange={(e) => setSelectedAgent(e.target.value)}
          className="w-full max-w-md px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          {agents.map(agent => (
            <option key={agent.id} value={agent.id}>
              {agent.full_name}
            </option>
          ))}
        </select>
      </div>

      {/* Sub Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex -mb-px">
          <button
            onClick={() => setSubTab('weekly')}
            className={`px-6 py-4 text-sm font-medium border-b-2 ${
              subTab === 'weekly'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Weekly Schedule
          </button>
          <button
            onClick={() => setSubTab('timeoff')}
            className={`px-6 py-4 text-sm font-medium border-b-2 ${
              subTab === 'timeoff'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Time Off
          </button>
          <button
            onClick={() => setSubTab('overrides')}
            className={`px-6 py-4 text-sm font-medium border-b-2 ${
              subTab === 'overrides'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Overrides
          </button>
        </nav>
      </div>

      <div className="p-6">
        {subTab === 'weekly' && (
          <WeeklyScheduleTab
            weeklySchedule={weeklySchedule}
            daysOfWeek={daysOfWeek}
            onScheduleUpdate={handleScheduleUpdate}
            agentName={selectedAgentName}
          />
        )}
        {subTab === 'timeoff' && (
          <TimeOffTab
            timeOffRecords={timeOffRecords}
            selectedAgent={selectedAgent}
            agentName={selectedAgentName}
            onRefresh={loadAgentData}
          />
        )}
        {subTab === 'overrides' && (
          <ScheduleOverridesTab
            scheduleOverrides={scheduleOverrides}
            selectedAgent={selectedAgent}
            agentName={selectedAgentName}
            onRefresh={loadAgentData}
          />
        )}
      </div>
    </div>
  );
};

// Weekly Schedule Tab Component
const WeeklyScheduleTab = ({ weeklySchedule, daysOfWeek, onScheduleUpdate, agentName }) => {
  return (
    <div>
      <h2 className="text-xl font-semibold mb-4">Weekly Schedule for {agentName}</h2>
      <p className="text-gray-600 mb-6">Set the default working hours for each day of the week</p>
      
      <div className="space-y-4">
        {weeklySchedule.map((daySchedule) => (
          <DayScheduleRow
            key={daySchedule.day_of_week}
            daySchedule={daySchedule}
            dayName={daysOfWeek[daySchedule.day_of_week]}
            onUpdate={onScheduleUpdate}
          />
        ))}
      </div>
    </div>
  );
};

// Day Schedule Row Component
const DayScheduleRow = ({ daySchedule, dayName, onUpdate }) => {
  const [isAvailable, setIsAvailable] = useState(daySchedule.is_available);
  const [startTime, setStartTime] = useState(daySchedule.start_time || '08:00');
  const [endTime, setEndTime] = useState(daySchedule.end_time || '18:00');

  const handleSave = () => {
    onUpdate(daySchedule.day_of_week, 'is_available', isAvailable);
    if (isAvailable) {
      onUpdate(daySchedule.day_of_week, 'start_time', startTime);
      onUpdate(daySchedule.day_of_week, 'end_time', endTime);
    }
  };

  return (
    <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
      <div className="w-32 font-medium text-gray-900">{dayName}</div>
      
      <label className="flex items-center gap-2">
        <input
          type="checkbox"
          checked={isAvailable}
          onChange={(e) => setIsAvailable(e.target.checked)}
          className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
        />
        <span className="text-sm text-gray-700">Available</span>
      </label>

      {isAvailable && (
        <>
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600">From:</label>
            <input
              type="time"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600">To:</label>
            <input
              type="time"
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </>
      )}

      <button
        onClick={handleSave}
        className="ml-auto px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
      >
        Save
      </button>
    </div>
  );
};

// Time Off Tab Component
const TimeOffTab = ({ timeOffRecords, selectedAgent, agentName, onRefresh }) => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({
    start_date: '',
    end_date: '',
    all_day: true,
    start_time: '08:00',
    end_time: '18:00',
    reason: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await agentAvailabilityService.addTimeOff({
        bail_agent_id: selectedAgent,
        ...formData,
        start_time: formData.all_day ? null : formData.start_time,
        end_time: formData.all_day ? null : formData.end_time
      });
      setShowAddForm(false);
      setFormData({
        start_date: '',
        end_date: '',
        all_day: true,
        start_time: '08:00',
        end_time: '18:00',
        reason: ''
      });
      onRefresh();
      alert('Time off added successfully!');
    } catch (error) {
      console.error('Error adding time off:', error);
      alert('Error adding time off. Please try again.');
    }
  };

  const handleDelete = async (timeOffId) => {
    if (confirm('Are you sure you want to delete this time off record?')) {
      try {
        await agentAvailabilityService.deleteTimeOff(timeOffId);
        onRefresh();
        alert('Time off deleted successfully!');
      } catch (error) {
        console.error('Error deleting time off:', error);
        alert('Error deleting time off. Please try again.');
      }
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-xl font-semibold">Time Off & Vacations for {agentName}</h2>
          <p className="text-gray-600 mt-1">Manage days off, vacations, and holidays</p>
        </div>
        <button
          onClick={() => setShowAddForm(!showAddForm)}
          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
        >
          {showAddForm ? 'Cancel' : '+ Add Time Off'}
        </button>
      </div>

      {showAddForm && (
        <form onSubmit={handleSubmit} className="bg-blue-50 p-6 rounded-lg mb-6">
          <h3 className="text-lg font-semibold mb-4">Add New Time Off</h3>
          
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Start Date
              </label>
              <input
                type="date"
                required
                value={formData.start_date}
                onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                End Date
              </label>
              <input
                type="date"
                required
                value={formData.end_date}
                onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div className="mb-4">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.all_day}
                onChange={(e) => setFormData({ ...formData, all_day: e.target.checked })}
                className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
              />
              <span className="text-sm font-medium text-gray-700">All Day</span>
            </label>
          </div>

          {!formData.all_day && (
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Start Time
                </label>
                <input
                  type="time"
                  value={formData.start_time}
                  onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  End Time
                </label>
                <input
                  type="time"
                  value={formData.end_time}
                  onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          )}

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Reason (Optional)
            </label>
            <input
              type="text"
              value={formData.reason}
              onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
              placeholder="e.g., Vacation, Holiday, Personal Day"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <button
            type="submit"
            className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Add Time Off
          </button>
        </form>
      )}

      <div className="space-y-3">
        {timeOffRecords.length === 0 ? (
          <p className="text-gray-500 text-center py-8">No time off records found</p>
        ) : (
          timeOffRecords.map((record) => (
            <div key={record.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <div className="font-medium text-gray-900">
                  {new Date(record.start_date).toLocaleDateString()} - {new Date(record.end_date).toLocaleDateString()}
                </div>
                <div className="text-sm text-gray-600">
                  {record.all_day ? 'All Day' : `${record.start_time} - ${record.end_time}`}
                  {record.reason && ` • ${record.reason}`}
                </div>
              </div>
              <button
                onClick={() => handleDelete(record.id)}
                className="px-3 py-1 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              >
                Delete
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

// Schedule Overrides Tab Component
const ScheduleOverridesTab = ({ scheduleOverrides, selectedAgent, agentName, onRefresh }) => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({
    override_date: '',
    is_available: true,
    start_time: '08:00',
    end_time: '18:00',
    reason: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await agentAvailabilityService.addScheduleOverride({
        bail_agent_id: selectedAgent,
        ...formData,
        start_time: formData.is_available ? formData.start_time : null,
        end_time: formData.is_available ? formData.end_time : null
      });
      setShowAddForm(false);
      setFormData({
        override_date: '',
        is_available: true,
        start_time: '08:00',
        end_time: '18:00',
        reason: ''
      });
      onRefresh();
      alert('Schedule override added successfully!');
    } catch (error) {
      console.error('Error adding override:', error);
      alert('Error adding schedule override. Please try again.');
    }
  };

  const handleDelete = async (overrideId) => {
    if (confirm('Are you sure you want to delete this schedule override?')) {
      try {
        await agentAvailabilityService.deleteScheduleOverride(overrideId);
        onRefresh();
        alert('Schedule override deleted successfully!');
      } catch (error) {
        console.error('Error deleting override:', error);
        alert('Error deleting schedule override. Please try again.');
      }
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-xl font-semibold">Schedule Overrides for {agentName}</h2>
          <p className="text-gray-600 mt-1">One-time schedule changes for specific dates</p>
        </div>
        <button
          onClick={() => setShowAddForm(!showAddForm)}
          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
        >
          {showAddForm ? 'Cancel' : '+ Add Override'}
        </button>
      </div>

      {showAddForm && (
        <form onSubmit={handleSubmit} className="bg-blue-50 p-6 rounded-lg mb-6">
          <h3 className="text-lg font-semibold mb-4">Add Schedule Override</h3>
          
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Date
            </label>
            <input
              type="date"
              required
              value={formData.override_date}
              onChange={(e) => setFormData({ ...formData, override_date: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="mb-4">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.is_available}
                onChange={(e) => setFormData({ ...formData, is_available: e.target.checked })}
                className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
              />
              <span className="text-sm font-medium text-gray-700">Available on this date</span>
            </label>
          </div>

          {formData.is_available && (
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Start Time
                </label>
                <input
                  type="time"
                  value={formData.start_time}
                  onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  End Time
                </label>
                <input
                  type="time"
                  value={formData.end_time}
                  onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          )}

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Reason (Optional)
            </label>
            <input
              type="text"
              value={formData.reason}
              onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
              placeholder="e.g., Extended hours for special event"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <button
            type="submit"
            className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Add Override
          </button>
        </form>
      )}

      <div className="space-y-3">
        {scheduleOverrides.length === 0 ? (
          <p className="text-gray-500 text-center py-8">No schedule overrides found</p>
        ) : (
          scheduleOverrides.map((override) => (
            <div key={override.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <div className="font-medium text-gray-900">
                  {new Date(override.override_date).toLocaleDateString()}
                </div>
                <div className="text-sm text-gray-600">
                  {override.is_available 
                    ? `${override.start_time} - ${override.end_time}` 
                    : 'Not Available'}
                  {override.reason && ` • ${override.reason}`}
                </div>
              </div>
              <button
                onClick={() => handleDelete(override.id)}
                className="px-3 py-1 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              >
                Delete
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default SEODashboard;
